import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Mail, Phone, MapPin, Send, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const defaultContent = {
  title: "Get In Touch",
  subtitle: "Have a question? We'd love to hear from you. Send us a message and we'll respond as soon as possible.",
  form_title: "Send Us a Message",
  contact_email: "support@indicraft.in",
  contact_phone: "+91 123-456-7890",
  address_line1: "123 Business Park",
  address_line2: "Mumbai, Maharashtra 400001",
  address_line3: "India",
  whatsapp_number: "+911234567890",
  whatsapp_message: "Hi, I have a question about...",
  business_hours: "Monday - Saturday: 9:00 AM - 7:00 PM\nSunday: 10:00 AM - 5:00 PM",
  meta_title: "Contact Us | Indicraft",
  meta_description: "Contact Indicraft for any queries about orders, products, or support."
};

const ContactPage = () => {
  const [content, setContent] = useState(defaultContent);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchContent();
  }, []);

  useEffect(() => {
    document.title = content.meta_title || "Contact Us | Indicraft";
  }, [content]);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/cms/contact`);
      if (response.data) {
        setContent({ ...defaultContent, ...response.data });
      }
    } catch (error) {
      console.log('Using default content for Contact page');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API_URL}/api/contact/submit`, formData);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleWhatsApp = () => {
    const message = encodeURIComponent(content.whatsapp_message);
    const number = content.whatsapp_number.replace(/[^0-9]/g, '');
    window.open(`https://wa.me/${number}?text=${message}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#064E3B]"></div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      
      <main className="flex-grow">
        <div className="bg-gradient-to-b from-white to-[#F8FAFC] py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold text-[#0f172a] mb-4" data-testid="contact-title">
                {content.title}
              </h1>
              <p className="text-lg text-zinc-600">
                {content.subtitle}
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <div className="bg-white rounded-2xl shadow-sm border border-zinc-200 p-8">
                  <h2 className="text-2xl font-semibold text-[#0f172a] mb-6">{content.form_title}</h2>
                  
                  <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          data-testid="contact-name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email">Email Address *</Label>
                        <Input
                          id="email"
                          type="email"
                          data-testid="contact-email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required
                          className="mt-1"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <Label htmlFor="phone">Phone Number</Label>
                        <Input
                          id="phone"
                          type="tel"
                          data-testid="contact-phone"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="mt-1"
                          placeholder="+91 XXXXXXXXXX"
                        />
                      </div>
                      <div>
                        <Label htmlFor="subject">Subject *</Label>
                        <Input
                          id="subject"
                          data-testid="contact-subject"
                          value={formData.subject}
                          onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                          required
                          className="mt-1"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        data-testid="contact-message"
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        required
                        rows={6}
                        className="mt-1"
                        placeholder="Tell us how we can help you..."
                      />
                    </div>

                    <Button
                      type="submit"
                      data-testid="contact-submit"
                      disabled={submitting}
                      className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-4 rounded-full text-lg"
                    >
                      <Send className="w-5 h-5 mr-2" />
                      {submitting ? 'Sending...' : 'Send Message'}
                    </Button>
                  </form>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-white rounded-2xl shadow-sm border border-zinc-200 p-6">
                  <h3 className="text-xl font-semibold text-[#0f172a] mb-4">Contact Information</h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#F0FDF4] flex items-center justify-center flex-shrink-0">
                        <Mail className="w-5 h-5 text-[#064E3B]" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[#0f172a]">Email</p>
                        <a href={`mailto:${content.contact_email}`} className="text-sm text-zinc-600 hover:text-[#064E3B]">
                          {content.contact_email}
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#F0FDF4] flex items-center justify-center flex-shrink-0">
                        <Phone className="w-5 h-5 text-[#064E3B]" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[#0f172a]">Phone</p>
                        <a href={`tel:${content.contact_phone}`} className="text-sm text-zinc-600 hover:text-[#064E3B]">
                          {content.contact_phone}
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#F0FDF4] flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-[#064E3B]" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[#0f172a]">Address</p>
                        <p className="text-sm text-zinc-600">
                          {content.address_line1}<br />
                          {content.address_line2}<br />
                          {content.address_line3}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-[#F0FDF4] rounded-2xl border border-[#86EFAC] p-6">
                  <h3 className="text-xl font-semibold text-[#0f172a] mb-3">Chat on WhatsApp</h3>
                  <p className="text-sm text-zinc-700 mb-4">
                    Get instant support via WhatsApp. We're here to help!
                  </p>
                  <Button
                    onClick={handleWhatsApp}
                    data-testid="whatsapp-button"
                    className="w-full bg-[#25D366] hover:bg-[#20BA5A] text-white font-semibold py-3 rounded-full"
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    Chat on WhatsApp
                  </Button>
                </div>

                <div className="bg-[#FEF3C7] rounded-2xl border border-[#FDE047] p-6">
                  <h3 className="text-lg font-semibold text-[#0f172a] mb-2">Business Hours</h3>
                  <p className="text-sm text-zinc-700 whitespace-pre-line">
                    {content.business_hours}
                  </p>
                  <p className="text-xs text-zinc-500 mt-2">(Indian Standard Time)</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;
